import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
    Table
} from "reactstrap";
import { useFormik } from "formik";
import { queryKeyes } from "shared/queryKeys";
import { useSelector } from "react-redux";
import { RootState } from "index";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { useQuery } from "react-query";
import { customStyle } from "shared/CommonCSS";
import Select from "react-select";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import * as Yup from "yup";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import ErrorTooltip from "Components/ErrorTooltip";
import apiGlobal from "global/api.global";
import VesselDetailsHeader from "Components/VesselDetailsHeader";
import Logo from '../Media/LogoLogin.png';
import { errorToast } from "Components/Toasts";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Message from "Components/Message";
import { dateTimeFormat } from "GenericForms/Helper";
interface FOROBDataType {
    activeTab: string
}

const FOROBData = ({ activeTab }: FOROBDataType) => {
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [tableData, setTableData] = useState<any[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [error, setError] = useState("");
    // Fetch fuel oil options
    const { data: fuelOilOptions, isLoading: isFuelOilOptionsLoading } = useQuery(
        [queryKeyes.vessel.vesselFuelType.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.vesselFuelType.url(VesselID), VesselID);
        },
        {
            enabled: true,
            staleTime: Infinity
        }
    );

    // FORM
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vessel: VesselID,
            fuelType: [],
            startDate: "",
            endDate: "",
        },
        validationSchema: Yup.object().shape({
            startDate: Yup.date().required(commonValidationMessages.required),
            endDate: Yup.date().required(commonValidationMessages.required),
            fuelType: Yup.array()
                .min(1, commonValidationMessages.required)
                .required(commonValidationMessages.required),
        }),
        onSubmit: async (values) => {
            setIsDataLoading(true);
            setTableData([]);

            try {
                const res = await apiGlobal.post(
                    'rob/report_batch_rob/get_bacth_wise_consumption/',
                    values
                );

                // store table rows in state
                setError("");
                setTableData(res.data);

            } catch (error: any) {
                errorToast(error?.response?.data?.message || "Something went wrong");
                setError(error?.response?.data?.message || "Something went wrong");
            } finally {
                setIsDataLoading(false);
            }
        }

    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        formik.handleSubmit();
    };
    // Extract all unique batch_ids across all reports
    const uniqueBatchIds = React.useMemo(() => {
        const ids = new Set<number>();

        tableData.forEach(report => {
            report.batches.forEach((b: any) => ids.add(b.batch_id));
        });

        return Array.from(ids);
    }, [tableData]);

    const downloadExcel = async () => {
        if (!tableData || tableData.length === 0) {
            errorToast("No data available!");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Fuel Oil ROB");

        // --------------------------------------------------------------------
        // 1) DYNAMIC COLUMN SETUP
        // --------------------------------------------------------------------
        // Structure: # (1) + Report Name (1) + (5 cols per batch)
        const batchColsCount = uniqueBatchIds.length * 5;
        const totalColumns = 2 + batchColsCount;

        // Define column widths
        const columnsConfig: any[] = [
            { width: 8 },  // #
            { width: 30 }, // Report Name
        ];

        // Add 5 columns for each batch
        uniqueBatchIds.forEach(() => {
            columnsConfig.push(
                { width: 50 }, // Batch Name
                { width: 15 }, // Consumption
                { width: 15 }, // Adjustment
                { width: 10 }, // Debunkering
                { width: 15 }  // ROB
            );
        });

        sheet.columns = columnsConfig;

        // --------------------------------------------------------------------
        // 2) MAIN TITLE
        // --------------------------------------------------------------------
        // Merge from Row 1, Col 1 -> Row 1, Col (totalColumns)
        sheet.mergeCells(1, 1, 1, totalColumns);

        const titleCell = sheet.getCell(1, 1);
        titleCell.value = "Fuel Oil ROB Report";
        titleCell.font = { size: 18, bold: true, color: { argb: "FFFFFFFF" } };
        titleCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF0066CC" }
        };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        sheet.getRow(1).height = 30;

        // --------------------------------------------------------------------
        // 3) REPORT DETAILS (Metadata)
        // --------------------------------------------------------------------
        sheet.addRow([]); // Row 2 (Spacer)

        // Helper to add detail rows with specific styling
        // We assume Labels take up Col 1-2, Values take up Col 3-Total
        const addDetailRow = (label: string, value: string) => {
            const row = sheet.addRow([label, "", value]);
            const rIdx = row.number;

            // Merge Label (Col 1 to 2)
            sheet.mergeCells(rIdx, 1, rIdx, 2);
            // Merge Value (Col 3 to Total)
            sheet.mergeCells(rIdx, 3, rIdx, totalColumns);

            // Style Label
            const labelCell = sheet.getCell(rIdx, 1);
            labelCell.font = { bold: true, size: 11 };
            labelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE7E6E6" } };
            labelCell.alignment = { horizontal: "right", vertical: "middle" };

            // Style Value
            const valueCell = sheet.getCell(rIdx, 3);
            valueCell.font = { size: 11 };
            valueCell.alignment = { horizontal: "left", vertical: "middle" };
        };

        // Prepare Data
        const selectedFuelNames = formik.values.fuelType
            .map((fuelId: string) => {
                const fuelOption = fuelOilOptions?.find((opt: any) => opt.fuel_type === fuelId);
                return fuelOption?.fuel_type_name || fuelId;
            })
            .join(", ");

        const formatDate = (dateStr: string) => {
            if (!dateStr) return "N/A";
            return new Date(dateStr).toLocaleDateString("en-GB", {
                day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
            });
        };

        // Add Rows 3, 4, 5
        addDetailRow("Fuel Type(s):", selectedFuelNames || "All");
        addDetailRow("Date Range:", `${formatDate(formik.values.startDate)} - ${formatDate(formik.values.endDate)}`);
        addDetailRow("Generated On:", new Date().toLocaleString("en-GB"));

        sheet.addRow([]); // Row 6 (Spacer)

        // --------------------------------------------------------------------
        // 4) HEADER ROW
        // --------------------------------------------------------------------
        const headerLabels = ["#", "Report Name"];
        uniqueBatchIds.forEach(() => {
            headerLabels.push("Batch", "Consumption", "Adjustment", "Debunkering", "ROB");
        });

        const headerRow = sheet.addRow(headerLabels);
        headerRow.height = 25;

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
                top: { style: "thin", color: { argb: "FF000000" } },
                left: { style: "thin", color: { argb: "FF000000" } },
                bottom: { style: "thin", color: { argb: "FF000000" } },
                right: { style: "thin", color: { argb: "FF000000" } }
            };
        });

        // --------------------------------------------------------------------
        // 5) DATA ROWS
        // --------------------------------------------------------------------
        tableData.forEach((row, index) => {
            const rowValues = [
                index + 1,
                `${row.report_name} (${row.report_time})`,
            ];

            uniqueBatchIds.forEach((id) => {
                const batch = row.batches.find((b: any) => b.batch_id === id);
                rowValues.push(
                    batch?.batch_name ?? "-",
                    batch?.consumption ?? "-",
                    batch?.adjustment ?? "-",
                    batch?.debunkering ?? "-",
                    batch?.rob ?? "-"
                );
            });

            const dataRow = sheet.addRow(rowValues);
            const fillColor = index % 2 === 0 ? "FFFFFFFF" : "FFF2F2F2";

            dataRow.eachCell((cell, colNumber) => {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFD3D3D3" } },
                    left: { style: "thin", color: { argb: "FFD3D3D3" } },
                    bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
                    right: { style: "thin", color: { argb: "FFD3D3D3" } }
                };

                if (colNumber === 1) cell.alignment = { horizontal: "center", vertical: "middle" };
                else if (colNumber === 2) cell.alignment = { horizontal: "left", vertical: "middle" };
                else cell.alignment = { horizontal: "right", vertical: "middle" };
            });
        });

        // --------------------------------------------------------------------
        // 6) FOOTER WITH LOGO
        // --------------------------------------------------------------------
        sheet.addRow([]);
        sheet.addRow([]);

        // Prepare Base64 Logo
        let logoBase64 = "";
        try {
            const response = await fetch(Logo);
            const blob = await response.blob();
            logoBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result?.toString().split(",")[1]);
                reader.readAsDataURL(blob);
            }) as string;
        } catch (e) {
            console.warn("Could not load logo for Excel", e);
        }

        if (logoBase64) {
            const imageId = workbook.addImage({
                base64: logoBase64,
                extension: "png",
            });

            // Calculate Footer Row Index (next available row)
            const footerRowIndex = sheet.rowCount + 1;

            // Set Text in Column 2 (B)
            const footerCell = sheet.getCell(footerRowIndex, 2);
            footerCell.value = "Created by ecoSAIL Infotech";
            footerCell.font = { italic: true, size: 11, color: { argb: "FF666666" } };
            footerCell.alignment = { horizontal: "left", vertical: "middle" };

            // Merge Text Cells (Col 2 to 5) - Numeric merge prevents "already merged" errors
            // Ensure we don't merge beyond total columns if table is small
            const mergeEndCol = Math.min(5, totalColumns);
            if (mergeEndCol > 2) {
                sheet.mergeCells(footerRowIndex, 2, footerRowIndex, mergeEndCol);
            }

            // Set Row Height
            sheet.getRow(footerRowIndex).height = 40;

            // Place Image in Column 1 (A)
            // tl: { col: 0, row: ... } is 0-based index
            sheet.addImage(imageId, {
                tl: { col: 0, row: footerRowIndex - 1 },
                ext: { width: 35, height: 35 },
                editAs: undefined // prevent excel from trying to lock it
            });
        }

        // --------------------------------------------------------------------
        // 7) SAVE
        // --------------------------------------------------------------------
        const buffer = await workbook.xlsx.writeBuffer();
        const blobExcel = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blobExcel, "Fuel_Oil_ROB_Report.xlsx");
    };


    /**delete data when vessel changes */
    useEffect(() => {
        setTableData([]);
    }, [VesselID]);

    return (
        <Card>
            <CardBody>
                <Row className="mb-2 p-2">
                    <Col sm={3}>
                        <h5 className="page_title pos-start mt-2">Fuel Oil ROB Data</h5>
                    </Col>
                    <Col sm={9}>
                        <VesselDetailsHeader />
                    </Col>
                </Row>
                {/* FILTER ROW */}
                <form onSubmit={handleSubmit}>
                    <Row className="align-items-end">
                        {/* Fuel Type */}
                        <Col md="3">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 asteric">Select Fuel Oil</Label>
                                <Select
                                    inputId="SelectedFuel"
                                    options={
                                        fuelOilOptions?.map((item: any) => ({
                                            value: item.fuel_type,
                                            label: item.fuel_type_name
                                        })) ?? []
                                    }
                                    isMulti
                                    styles={customStyle}
                                    isLoading={isFuelOilOptionsLoading}
                                    onChange={(selected) =>
                                        formik.setFieldValue(
                                            "fuelType",
                                            selected ? selected.map((s) => s.value) : []
                                        )
                                    }
                                    value={
                                        formik.values.fuelType.map((id) => {
                                            const opt = fuelOilOptions?.find((o: any) => o.fuel_type === id);
                                            return opt ? { value: opt.fuel_type, label: opt.fuel_type_name } : null;
                                        }).filter(Boolean)
                                    }
                                    onBlur={() => formik.setFieldTouched("fuelType", true)}
                                />
                            </FormGroup>
                            {
                                formik.touched.fuelType && formik.errors.fuelType && activeTab === '1' && (
                                    <ErrorTooltip
                                        message={formik.errors.fuelType}
                                        target={"SelectedFuel"}
                                        open={(formik.errors && formik.errors.fuelType) ? true : false}
                                    />
                                )
                            }
                        </Col>

                        {/* Start Date */}
                        <Col md="3">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 asteric">Start Date</Label>
                                <Input
                                    type="datetime-local"
                                    name="startDate"
                                    id='startDate_fo'
                                    className="datetimepicker text-uppercase"
                                    onFocus={(e) => e.target.select()}
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                    onBlur={(e) => {
                                        formik.setFieldTouched("startDate")
                                        formik.handleBlur(e)
                                    }}
                                    max={"9999-12-31T00:00"}
                                />
                            </FormGroup>
                            {formik.touched.startDate && formik.errors.startDate && activeTab === '1' && (
                                <ErrorTooltip
                                    message={formik.errors.startDate}
                                    target={"startDate_fo"}
                                    open={(formik.errors && formik.errors.startDate) ? true : false}
                                />
                            )}
                        </Col>

                        {/* End Date */}
                        <Col md="3">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 asteric">End Date</Label>
                                <Input
                                    type="datetime-local"
                                    name="endDate"
                                    id='endDate_fo'
                                    className="datetimepicker text-uppercase"
                                    onFocus={(e) => e.target.select()}
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                    onBlur={(e) => {
                                        formik.setFieldTouched("endDate")
                                        formik.handleBlur(e)
                                    }}
                                    max={"9999-12-31T00:00"}
                                />
                            </FormGroup>
                            {formik.touched.endDate && formik.errors.endDate && activeTab === '1' && (
                                <ErrorTooltip
                                    message={formik.errors.endDate}
                                    target={"endDate_fo"}
                                    open={(formik.errors && formik.errors.endDate) ? true : false}
                                />
                            )}
                        </Col>

                        {/* GO Button */}
                        <Col md="2" className="d-flex align-items-end">
                            <Button
                                type="submit"
                                color="primary"
                                size="md"
                                className="mb-3"
                                disabled={formik.isSubmitting || !formik.isValid}
                            >
                                Generate
                            </Button>
                        </Col>
                        <FormValuesDebug values={[formik.values, formik.errors, formik.touched]} />
                    </Row>
                </form>

                {/* TABLE WITH HORIZONTAL SCROLL */}
                {(error && !isDataLoading) && <Message message={error} type="danger" />}
                {
                    !isDataLoading && tableData.length > 0 && (
                        <Row className="mt-4">
                            <div className="table-scroll-wrapper">
                                <Table bordered className="p-0 m-0 table-wide">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="sr-no-width">#</th>
                                            <th className="text-center">Report Name</th>

                                            {/* show up to 10 batches across horizontally */}
                                            {uniqueBatchIds.map((id) => (
                                                <React.Fragment key={id}>
                                                    <th className="text-center bg-blue-light">Batch </th>
                                                    <th className="text-center bg-blue-light">Consumption</th>
                                                    <th className="text-center bg-blue-light">Adjustment</th>
                                                    <th className="text-center bg-blue-light">Debunkering</th>
                                                    <th className="text-center bg-blue-light">ROB</th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {tableData.map((row, index) => (
                                            <tr key={index}>
                                                <td >{index + 1}</td>
                                                <td>
                                                    {row.report_name} ({dateTimeFormat(row.report_time, false, false, true)})
                                                </td>

                                                {/* render the real batches */}
                                                {uniqueBatchIds.map((id) => {
                                                    const batch = row.batches.find((b: any) => b.batch_id === id);

                                                    return (
                                                        <React.Fragment key={id}>
                                                            <td className="text-center" style={{ minWidth: "200px" }}>
                                                                {batch ? batch.batch_name : "-"}
                                                            </td>
                                                            <td className="text-center">{batch ? batch.consumption : "-"}</td>
                                                            <td className="text-center">{batch ? batch.adjustment : "-"}</td>
                                                            <td className="text-center">{batch ? batch.debunkering : "-"}</td>
                                                            <td className="text-center"><b>{batch ? batch.rob : "-"}</b></td>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            {/* DOWNLOAD BUTTON */}
                            {tableData?.length > 0 && (
                                <Row className="mt-4 justify-content-end">
                                    <Col md="2" className="text-end">
                                        <div className="justify_right">
                                            <button
                                                type="button"
                                                id="download"
                                                className="btn btn-primary waves-effect btn-label waves-light mb-2"
                                                onClick={downloadExcel}
                                            >
                                                <i className="mdi mdi-download label-icon" />
                                                Download
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Row>
                    )
                }
            </CardBody>
        </Card>
    );
};

export default FOROBData;